/**
 * Timbre presets — oscillator + simple ADSR + filter shape per voice.
 * Intentionally small palette (a handful of character-rich presets)
 * rather than a synth editor; the player cycles between them with a
 * hand gesture, so the distinctions must be immediately audible.
 */

export interface TimbrePreset {
  key: string;
  label: string;
  /** Short, one-word mood tag for the on-stage toast. */
  tag: string;
  osc1: OscillatorType;
  osc2: OscillatorType;
  /** Cents detune for osc2 — bigger = richer/phaser-like, 0 = unison. */
  detuneCents: number;
  /** Gain at the peak of the attack (multiplied by velocity-dependent
   *  base). Keep ≤ 1 to leave headroom for the limiter. */
  peakGainScale: number;
  attackMs: number;
  decayMs: number;
  /** Sustain fraction of peak that the voice holds before its tail. */
  sustainFrac: number;
  /** Full tail length in seconds (voice fades to 0 over this). */
  tailSec: number;
  filterType: BiquadFilterType;
  filterQ: number;
  /** Cutoff at attack peak = base * mult + velocity * vboost. */
  cutoffMult: number;
  cutoffVelBoost: number;
  /** Cutoff at end-of-decay (min clamp at 220 Hz applied elsewhere). */
  cutoffTailMult: number;
  /** 0..1 — how much of the voice is sent to the shared wet bus. */
  wet: number;
}

export const TIMBRE_PRESETS: readonly TimbrePreset[] = [
  {
    key: 'zither',
    label: 'Zither',
    tag: 'đàn tranh',
    osc1: 'sawtooth',
    osc2: 'triangle',
    detuneCents: 3,
    peakGainScale: 1.0,
    attackMs: 15,
    decayMs: 500,
    sustainFrac: 0.7,
    tailSec: 3.5,
    filterType: 'lowpass',
    filterQ: 5,
    cutoffMult: 10,
    cutoffVelBoost: 3500,
    cutoffTailMult: 1.8,
    wet: 0.1,
  },
  {
    key: 'bell',
    label: 'Bell',
    tag: 'sine bell',
    osc1: 'sine',
    osc2: 'sine',
    detuneCents: 7,
    peakGainScale: 0.85,
    attackMs: 4,
    decayMs: 1400,
    sustainFrac: 0.15,
    tailSec: 5.0,
    filterType: 'lowpass',
    filterQ: 0.7,
    cutoffMult: 16,
    cutoffVelBoost: 1500,
    cutoffTailMult: 3,
    wet: 0.35,
  },
  {
    key: 'synth-pad',
    label: 'Pad',
    tag: 'warm pad',
    osc1: 'triangle',
    osc2: 'sawtooth',
    detuneCents: 12,
    peakGainScale: 0.75,
    attackMs: 120,
    decayMs: 900,
    sustainFrac: 0.55,
    tailSec: 4.0,
    filterType: 'lowpass',
    filterQ: 1.5,
    cutoffMult: 5,
    cutoffVelBoost: 1000,
    cutoffTailMult: 2.5,
    wet: 0.45,
  },
  {
    key: 'funk-bass',
    label: 'Funk',
    tag: 'punchy funk',
    osc1: 'square',
    osc2: 'sawtooth',
    detuneCents: 2,
    peakGainScale: 0.95,
    attackMs: 3,
    decayMs: 220,
    sustainFrac: 0.25,
    tailSec: 1.8,
    filterType: 'lowpass',
    filterQ: 8,
    cutoffMult: 6,
    cutoffVelBoost: 4500,
    cutoffTailMult: 1.2,
    wet: 0.05,
  },
];

export function getTimbreByKey(key: string): TimbrePreset {
  return TIMBRE_PRESETS.find((t) => t.key === key) ?? TIMBRE_PRESETS[0]!;
}
